import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  DialerCallDTO,
  DialerCallBranch,
  DialerCallStatus,
  DialerOutboundNumber,
} from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";

const keyOf = (n: DialerOutboundNumber) => `${n.connectionId}|${n.phoneNumberSid}`;

type ListState =
  | { status: "loading" }
  | { status: "ready"; calls: DialerCallDTO[] }
  | { status: "error"; message: string };

const POLL_MS = 2500;
const EMPTY_CALLS: DialerCallDTO[] = [];
const TERMINAL: ReadonlySet<DialerCallStatus> = new Set([
  "completed",
  "failed",
  "no_answer",
  "canceled",
]);

type StatusFilter = "all" | "live" | "done" | "failed";
type OutcomeFilter = "all" | DialerCallBranch;

/**
 * Outbound screened-dialer console (ADR 0024). Place a call, filter the log,
 * and open any call to inspect its screening timeline.
 */
export function OutboundDialerPage() {
  const [list, setList] = useState<ListState>({ status: "loading" });
  const [to, setTo] = useState("");
  const [numbers, setNumbers] = useState<DialerOutboundNumber[]>([]);
  const [fromKey, setFromKey] = useState("");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>("all");
  const [accountFilter, setAccountFilter] = useState<string>("all");

  // Detail slide-over
  const [openId, setOpenId] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.dialer
      .listCalls()
      .then((res) => {
        if (!cancelled) setList({ status: "ready", calls: res.calls });
      })
      .catch((err) => {
        if (!cancelled) {
          setList({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load calls",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api.dialer
      .listNumbers()
      .then((res) => {
        if (cancelled) return;
        setNumbers(res.numbers);
        if (res.numbers.length === 1) setFromKey(keyOf(res.numbers[0]!));
      })
      .catch(() => {
        /* leave numbers empty → guidance shown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const calls = useMemo(() => (list.status === "ready" ? list.calls : EMPTY_CALLS), [list]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return calls.filter((c) => {
      if (term && !c.to.toLowerCase().includes(term)) return false;
      if (statusFilter === "live" && TERMINAL.has(c.status)) return false;
      if (statusFilter === "done" && c.status !== "completed") return false;
      if (
        statusFilter === "failed" &&
        !(c.status === "failed" || c.status === "no_answer" || c.status === "canceled")
      )
        return false;
      if (outcomeFilter !== "all" && c.classification.branch !== outcomeFilter) return false;
      if (accountFilter !== "all" && c.connectionId !== accountFilter) return false;
      return true;
    });
  }, [calls, search, statusFilter, outcomeFilter, accountFilter]);

  // Distinct Twilio accounts present in the call log, for the account filter.
  const accountOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calls) {
      if (!m.has(c.connectionId)) m.set(c.connectionId, c.connectionName ?? "Account");
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [calls]);

  const pendingIds = useMemo(
    () =>
      calls
        .filter((c) => !TERMINAL.has(c.status))
        .map((c) => c.id)
        .sort()
        .join(","),
    [calls],
  );

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!pendingIds) return;
    const ids = pendingIds.split(",");
    pollRef.current = setInterval(() => {
      for (const id of ids) {
        void api.dialer
          .getCall(id)
          .then((updated) =>
            setList((prev) =>
              prev.status === "ready"
                ? {
                    status: "ready",
                    calls: prev.calls.map((c) => (c.id === updated.id ? updated : c)),
                  }
                : prev,
            ),
          )
          .catch(() => {
            /* transient */
          });
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [pendingIds]);

  const filtersActive =
    !!search.trim() || statusFilter !== "all" || outcomeFilter !== "all" || accountFilter !== "all";
  function clearFilters() {
    setSearch("");
    setStatusFilter("all");
    setOutcomeFilter("all");
    setAccountFilter("all");
  }

  async function place() {
    const trimmed = to.trim();
    const selected = numbers.find((n) => keyOf(n) === fromKey);
    if (!trimmed || !selected || placing) return;
    setPlacing(true);
    setError(null);
    try {
      const dto = await api.dialer.placeCall(selected.connectionId, selected.phoneNumberSid, {
        to: trimmed,
      });
      setTo("");
      setList((prev) =>
        prev.status === "ready"
          ? { status: "ready", calls: [dto, ...prev.calls] }
          : { status: "ready", calls: [dto] },
      );
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not place the call.");
    } finally {
      setPlacing(false);
    }
  }

  // Version of the open call from the list, so the drawer refreshes as polling lands.
  const openCallVersion = useMemo(
    () => (openId ? calls.find((c) => c.id === openId)?.updatedAt : undefined),
    [openId, calls],
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Dialer
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Screened Calls
          </h1>
          <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Place a screened outbound call. We detect whether a human, voicemail, or call screener
            answered — humans route to your configured handoff.
          </p>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <Link
            to="/dialer/settings"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2.5 py-1.5 text-[12.5px] font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-800/50"
          >
            Settings
          </Link>
          <Link
            to="/dialer/api"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2.5 py-1.5 text-[12.5px] font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-800/50"
          >
            API docs →
          </Link>
        </div>
      </header>

      {/* Place a call */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
        {numbers.length === 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-[13px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            No voice-capable numbers found. Connect a Twilio account with a voice number —{" "}
            <Link to="/connections" className="font-medium underline underline-offset-2">
              go to Connections
            </Link>
            .
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <label className="mb-1.5 block text-[12px] font-medium text-zinc-700 dark:text-zinc-300">
                Call from
              </label>
              <select
                value={fromKey}
                disabled={placing}
                onChange={(e) => setFromKey(e.target.value)}
                className="block w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-[14px] text-zinc-900 outline-none transition hover:border-zinc-300 focus-visible:border-brand-500 focus-visible:shadow-ring disabled:opacity-60 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:hover:border-zinc-700 dark:focus-visible:border-brand-500/70"
              >
                <option value="" disabled>
                  Choose a number…
                </option>
                {numbers.map((n) => (
                  <option key={keyOf(n)} value={keyOf(n)}>
                    {n.phoneNumber} · {n.connectionName}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[200px] flex-1">
              <Field
                label="Phone number to call"
                placeholder="+1 555 123 4567"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void place();
                }}
                disabled={placing}
              />
            </div>
            <Button onClick={() => void place()} disabled={placing || !to.trim() || !fromKey}>
              {placing ? "Placing…" : "Place call"}
            </Button>
          </div>
        )}
        {error && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        )}
      </div>

      {/* Filters */}
      {list.status === "ready" && calls.length > 0 && (
        <Toolbar
          search={search}
          onSearch={setSearch}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          outcomeFilter={outcomeFilter}
          onOutcomeFilter={setOutcomeFilter}
          accountFilter={accountFilter}
          onAccountFilter={setAccountFilter}
          accounts={accountOptions}
        />
      )}

      {/* Call log */}
      {list.status === "loading" && <SkeletonTable />}
      {list.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {list.message}
        </p>
      )}
      {list.status === "ready" && calls.length === 0 && <EmptyState />}
      {list.status === "ready" && calls.length > 0 && filtered.length === 0 && (
        <FilteredEmpty onClear={clearFilters} />
      )}
      {list.status === "ready" && filtered.length > 0 && (
        <CallTable calls={filtered} onOpen={setOpenId} activeId={openId} />
      )}

      {filtersActive && list.status === "ready" && (
        <p className="text-[12px] text-zinc-500">
          Showing {filtered.length} of {calls.length} call{calls.length === 1 ? "" : "s"}
        </p>
      )}

      <CallDetailDrawer callId={openId} version={openCallVersion} onClose={() => setOpenId(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function Toolbar({
  search,
  onSearch,
  statusFilter,
  onStatusFilter,
  outcomeFilter,
  onOutcomeFilter,
  accountFilter,
  onAccountFilter,
  accounts,
}: {
  search: string;
  onSearch: (s: string) => void;
  statusFilter: StatusFilter;
  onStatusFilter: (s: StatusFilter) => void;
  outcomeFilter: OutcomeFilter;
  onOutcomeFilter: (o: OutcomeFilter) => void;
  accountFilter: string;
  onAccountFilter: (a: string) => void;
  accounts: Array<{ id: string; name: string }>;
}) {
  const statuses: Array<{ value: StatusFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "live", label: "Live" },
    { value: "done", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search by number"
        aria-label="Search calls by destination number"
        className="h-9 min-w-[180px] max-w-xs flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70"
      />
      <div
        role="radiogroup"
        aria-label="Status filter"
        className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/40"
      >
        {statuses.map((s) => (
          <button
            key={s.value}
            type="button"
            role="radio"
            aria-checked={statusFilter === s.value}
            onClick={() => onStatusFilter(s.value)}
            className={
              "rounded-[5px] px-2.5 py-1 text-[12px] font-medium transition " +
              (statusFilter === s.value
                ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-50"
                : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100")
            }
          >
            {s.label}
          </button>
        ))}
      </div>
      <select
        value={outcomeFilter}
        onChange={(e) => onOutcomeFilter(e.target.value as OutcomeFilter)}
        aria-label="Outcome filter"
        className="h-9 rounded-md border border-zinc-200 bg-white px-2.5 text-[12.5px] text-zinc-800 outline-none transition hover:border-zinc-300 focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-200 dark:hover:border-zinc-700 dark:focus-visible:border-brand-500/70"
      >
        <option value="all">Any outcome</option>
        <option value="human">Human</option>
        <option value="voicemail">Voicemail</option>
        <option value="screener">Screener</option>
        <option value="interrupted">Interrupted</option>
        <option value="fallback">No audio</option>
      </select>
      {accounts.length > 1 && (
        <select
          value={accountFilter}
          onChange={(e) => onAccountFilter(e.target.value)}
          aria-label="Twilio account filter"
          className="h-9 max-w-[200px] rounded-md border border-zinc-200 bg-white px-2.5 text-[12.5px] text-zinc-800 outline-none transition hover:border-zinc-300 focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-200 dark:hover:border-zinc-700 dark:focus-visible:border-brand-500/70"
        >
          <option value="all">All accounts</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function CallTable({
  calls,
  onOpen,
  activeId,
}: {
  calls: DialerCallDTO[];
  onOpen: (id: string) => void;
  activeId: string | null;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
          <tr>
            <th className="px-4 py-2.5">To</th>
            <th className="hidden px-4 py-2.5 sm:table-cell">Account</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Outcome</th>
            <th className="hidden px-4 py-2.5 md:table-cell">Duration</th>
            <th className="hidden px-4 py-2.5 lg:table-cell">When</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
          {calls.map((c) => (
            <tr
              key={c.id}
              role="button"
              tabIndex={0}
              aria-label={`Open call to ${c.to}`}
              onClick={() => onOpen(c.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(c.id);
                }
              }}
              className={
                "cursor-pointer transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 " +
                (activeId === c.id
                  ? "bg-brand-50 dark:bg-brand-500/10"
                  : "hover:bg-zinc-50 dark:hover:bg-zinc-900/60")
              }
            >
              <td className="px-4 py-3 align-middle font-mono text-[13.5px] text-zinc-900 dark:text-zinc-100">
                <span className="inline-flex items-center gap-1.5">
                  {c.to}
                  <MetadataInfo metadata={c.metadata} />
                </span>
              </td>
              <td className="hidden max-w-[180px] truncate px-4 py-3 align-middle text-[12.5px] text-zinc-600 dark:text-zinc-400 sm:table-cell">
                {c.connectionName ?? "—"}
              </td>
              <td className="px-4 py-3 align-middle">
                <StatusBadge status={c.status} />
              </td>
              <td className="px-4 py-3 align-middle">
                {c.classification.branch ? (
                  <BranchBadge branch={c.classification.branch} />
                ) : (
                  <span className="text-[12px] text-zinc-400 dark:text-zinc-600">—</span>
                )}
              </td>
              <td className="hidden px-4 py-3 align-middle text-[12.5px] text-zinc-600 dark:text-zinc-400 md:table-cell">
                {c.durationSec != null ? `${c.durationSec}s` : "—"}
              </td>
              <td className="hidden px-4 py-3 align-middle text-[12.5px] text-zinc-500 lg:table-cell">
                {new Date(c.createdAt).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Info icon shown beside the destination when the call carries a metadata
 * payload. Shows the payload as a styled tooltip on hover/focus. Uses fixed
 * positioning so it isn't clipped by the table's `overflow-hidden`.
 */
function MetadataInfo({ metadata }: { metadata: Record<string, unknown> | null }) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  if (!metadata || Object.keys(metadata).length === 0) return null;
  const entries = Object.entries(metadata).map(([k, v]) => [k, formatMetaValue(v)] as const);

  const openAt = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setPos({ x: r.left, y: r.bottom + 6 });
  };

  return (
    <span className="inline-flex">
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => openAt(e.currentTarget)}
        onMouseLeave={() => setPos(null)}
        onFocus={(e) => openAt(e.currentTarget)}
        onBlur={() => setPos(null)}
        aria-label="View call metadata"
        className="inline-flex h-6 w-6 flex-none items-center justify-center rounded text-zinc-400 transition hover:bg-zinc-100 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-zinc-500 dark:hover:bg-zinc-800/60 dark:hover:text-brand-300"
      >
        <InfoIcon />
      </button>
      {pos && (
        <div
          role="tooltip"
          style={{ position: "fixed", left: pos.x, top: pos.y }}
          className="z-50 max-h-64 max-w-xs overflow-auto rounded-md border border-zinc-200 bg-white p-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
            Metadata
          </p>
          <dl className="space-y-1">
            {entries.map(([k, v]) => (
              <div key={k} className="flex gap-2 text-[11.5px] leading-snug">
                <dt className="flex-none font-medium text-zinc-500 dark:text-zinc-400">{k}</dt>
                <dd className="min-w-0 break-words font-mono text-zinc-800 dark:text-zinc-200">
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </span>
  );
}

/** Render a metadata value as a compact string; objects/arrays → JSON. */
function formatMetaValue(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v).slice(0, 200);
  } catch {
    return String(v);
  }
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="7.75" r="1" fill="currentColor" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Detail slide-over

type DetailState =
  | { status: "loading" }
  | { status: "ready"; call: DialerCallDTO }
  | { status: "error"; message: string };

function CallDetailDrawer({
  callId,
  version,
  onClose,
}: {
  callId: string | null;
  version: string | undefined;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<DetailState>({ status: "loading" });
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!callId) return;
    triggerRef.current = document.activeElement;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus();
    };
  }, [callId, onClose]);

  // Fetch (and refresh as the list version changes) while open.
  useEffect(() => {
    if (!callId) return;
    let cancelled = false;
    setDetail((prev) => (prev.status === "ready" ? prev : { status: "loading" }));
    void api.dialer
      .getCall(callId)
      .then((call) => {
        if (!cancelled) {
          setDetail({ status: "ready", call });
          closeRef.current?.focus();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load call",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [callId, version]);

  if (!callId) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Call detail"
      className="fixed inset-0 z-50 flex justify-end"
    >
      <div
        className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm dark:bg-zinc-950/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="relative flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white shadow-card-light dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card">
        <div className="flex items-start justify-between gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <div className="min-w-0">
            <h2 className="truncate font-mono text-[15px] font-semibold text-zinc-900 dark:text-zinc-50">
              {detail.status === "ready" ? detail.call.to : "Call"}
            </h2>
            {detail.status === "ready" && (
              <div className="mt-1.5 flex items-center gap-2">
                <StatusBadge status={detail.call.status} />
                {detail.call.classification.branch && (
                  <BranchBadge branch={detail.call.classification.branch} />
                )}
              </div>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 flex-none items-center justify-center rounded-md text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-200"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
              <path
                d="M18 6 6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {detail.status === "loading" && <DrawerSkeleton />}
          {detail.status === "error" && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {detail.message}
            </p>
          )}
          {detail.status === "ready" && <DetailBody call={detail.call} />}
        </div>
      </aside>
    </div>
  );
}

function DetailBody({ call }: { call: DialerCallDTO }) {
  const c = call.classification;
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-[13px]">
        <Row label="From" value={call.from} mono />
        <Row label="Account" value={call.connectionName ?? "—"} />
        <Row
          label="Outcome"
          value={
            c.branch
              ? `${c.branch}${c.decidedBy ? ` · by ${decidedByLabel(c.decidedBy)}` : ""}`
              : "—"
          }
        />
        <Row label="AMD" value={call.amd ?? "—"} />
        <Row label="Duration" value={call.durationSec != null ? `${call.durationSec}s` : "—"} />
        <Row
          label="Handoff"
          value={
            call.handoff
              ? `${call.handoff.provider ?? "?"}${call.handoff.ok === false ? " · failed" : call.handoff.ref ? " · " + call.handoff.ref : ""}`
              : "—"
          }
        />
        <Row label="Call SID" value={call.callSid ?? "—"} mono />
        <Row label="Started" value={new Date(call.createdAt).toLocaleString()} />
      </dl>

      {call.transcriptTail && (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            Transcript
          </p>
          <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12.5px] italic text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300">
            “{call.transcriptTail}”
          </p>
        </div>
      )}

      {c.phrase && c.phrase !== call.transcriptTail && (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            Classifier matched on
          </p>
          <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12.5px] italic text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300">
            “{c.phrase}”
          </p>
        </div>
      )}

      {call.metadata && Object.keys(call.metadata).length > 0 && (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            Context (sent with the call)
          </p>
          <pre className="overflow-x-auto rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11.5px] text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300">
            {JSON.stringify(call.metadata, null, 2)}
          </pre>
        </div>
      )}

      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Timeline
        </p>
        {call.events && call.events.length > 0 ? (
          <ol className="space-y-0">
            {call.events.map((e, i) => (
              <li key={i} className="flex gap-3 pb-3 last:pb-0">
                <div className="flex flex-col items-center">
                  <span
                    className={"mt-1 h-1.5 w-1.5 flex-none rounded-full " + eventDotClass(e.type)}
                  />
                  {call.events && i < call.events.length - 1 && (
                    <span className="mt-0.5 w-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
                  )}
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[12.5px] font-medium text-zinc-800 dark:text-zinc-200">
                      {eventLabel(e.type)}
                    </span>
                    <span className="flex-none text-[11px] text-zinc-400">
                      {new Date(e.at).toLocaleTimeString()}
                    </span>
                  </div>
                  {e.detail && (
                    <p className="mt-0.5 break-words text-[12px] text-zinc-500 dark:text-zinc-400">
                      {eventDetail(e.type, e.detail)}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-[12.5px] text-zinc-500">No events recorded yet.</p>
        )}
      </div>
    </div>
  );
}

const EVENT_LABELS: Record<string, string> = {
  created: "Call created",
  enqueued: "Queued",
  enqueue_failed: "Queue failed",
  placed: "Placed on Twilio",
  place_retry: "Placing — will retry",
  place_failed: "Place failed",
  answered: "Answered",
  interrupted: "Live pickup — interrupted",
  handoff_dispatched: "Handoff dispatched",
  handoff_failed: "Handoff failed",
  handoff_skipped: "Handoff skipped",
};

function eventLabel(type: string): string {
  const known = EVENT_LABELS[type];
  if (known) return known;
  if (type.startsWith("decided:")) return `Decided: ${type.slice("decided:".length)}`;
  if (type.startsWith("twilio:")) return `Twilio: ${type.slice("twilio:".length)}`;
  const spaced = type.replace(/[_:]/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Color the timeline dot by outcome so failures/skips/handoffs read at a glance. */
function eventDotClass(type: string): string {
  if (/fail|error/.test(type)) return "bg-red-500 dark:bg-red-400";
  if (/skip|retry/.test(type)) return "bg-amber-500 dark:bg-amber-400";
  if (/dispatch|interrupted|placed|answered/.test(type))
    return "bg-emerald-500 dark:bg-emerald-400";
  return "bg-brand-500 dark:bg-brand-400";
}

/** Humanize who decided the outcome. The classifier is shown generically as
 *  "AI" — we don't surface the underlying model ("claude" is a legacy value). */
function decidedByLabel(by: string): string {
  if (by === "ai" || by === "claude") return "AI";
  if (by === "amd") return "AMD";
  return by;
}

/** Detail text for a timeline event. For "decided:*" events the detail is the
 *  decider, shown generically as "AI" (never the underlying model). */
function eventDetail(type: string, detail: string): string {
  if (type.startsWith("decided:")) return decidedByLabel(detail);
  return detail;
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-zinc-500 dark:text-zinc-500">{label}</dt>
      <dd
        className={
          "min-w-0 break-words text-right text-zinc-800 dark:text-zinc-200 " +
          (mono ? "font-mono text-[12px]" : "")
        }
      >
        {value}
      </dd>
    </>
  );
}

// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<DialerCallStatus, string> = {
  queued: "Queued",
  placing: "Placing",
  initiated: "Calling",
  ringing: "Ringing",
  in_progress: "In progress",
  classifying: "Listening",
  classified: "Classified",
  handing_off: "Handing off",
  voicemail: "Voicemail",
  completed: "Completed",
  failed: "Failed",
  no_answer: "No answer",
  canceled: "Canceled",
};

function StatusBadge({ status }: { status: DialerCallStatus }) {
  const inFlight = !TERMINAL.has(status);
  const failed = status === "failed" || status === "no_answer" || status === "canceled";
  const cls = failed
    ? "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
    : inFlight
      ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300";
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium " +
        cls
      }
    >
      <span
        className={
          "h-1.5 w-1.5 rounded-full " +
          (failed ? "bg-red-500" : inFlight ? "bg-amber-500 animate-pulse" : "bg-emerald-500")
        }
      />
      {STATUS_LABEL[status]}
    </span>
  );
}

function BranchBadge({ branch }: { branch: DialerCallBranch }) {
  const map: Record<DialerCallBranch, { label: string; cls: string }> = {
    human: {
      label: "Human",
      cls: "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
    },
    voicemail: {
      label: "Voicemail",
      cls: "bg-zinc-100 text-zinc-600 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-400 dark:ring-zinc-700",
    },
    screener: {
      label: "Screener",
      cls: "bg-amber-50 text-amber-700 ring-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    },
    interrupted: {
      label: "Interrupted",
      cls: "bg-brand-50 text-brand-700 ring-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300",
    },
    fallback: {
      label: "No audio",
      cls: "bg-zinc-100 text-zinc-600 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-400 dark:ring-zinc-700",
    },
    unsure: {
      label: "Unsure",
      cls: "bg-zinc-100 text-zinc-600 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-400 dark:ring-zinc-700",
    },
  };
  const s = map[branch];
  return (
    <span className={"rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset " + s.cls}>
      {s.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-10 text-center dark:border-zinc-800 dark:bg-zinc-900/30">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">No calls yet</h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Place your first outbound call above. Each call's outcome — human, voicemail, or screener —
        shows up here as it's detected.
      </p>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center dark:border-zinc-800 dark:bg-zinc-900/30">
      <p className="text-[13px] text-zinc-600 dark:text-zinc-400">No calls match these filters.</p>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
      >
        Clear filters
      </button>
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 last:border-b-0 dark:border-zinc-800/80"
        >
          <div className="h-3 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="h-4 w-20 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800/70" />
        </div>
      ))}
    </div>
  );
}

function DrawerSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-4 w-full animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
      ))}
    </div>
  );
}
